/**
 * Phase 5B: paginated daily-P&L table.
 *
 * Columns: Date / Day / Daily P&L / Trades / Cumulative
 * Default sort: date desc (most recent first).
 *
 * Derived client-side from `aggregate.equity_curve` so we don't need a
 * second fetch. The cumulative column re-runs after sort so the displayed
 * value is the running total at that row's date.
 */
import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { MarketplaceEquityPoint } from '../../types/marketplaceAnalytics';
import { cn } from '../../utils/cn';
import { pnlClass } from '../../utils/platformFormat';

interface Props {
  equityCurve: MarketplaceEquityPoint[];
  pageSize?: number;
}

const PAGE_SIZE_DEFAULT = 30;

const dayOfWeek = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
};

const fmtInr = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MarketplaceDailyPnlTable: React.FC<Props> = ({
  equityCurve, pageSize = PAGE_SIZE_DEFAULT,
}) => {
  const [page, setPage] = useState(0);
  // Default ON — for most baskets, the rollup window has many days with
  // zero trades (weekends, holidays, days the strategy didn't fire). Hiding
  // them by default keeps the table focused on "days that actually traded";
  // the operator can flip the toggle when they need the full calendar.
  const [activeOnly, setActiveOnly] = useState(true);

  const allRows = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    // Equity curve is date-ascending; rebuild cumulative from the start so
    // we can sort desc afterwards without losing the running total.
    return [...equityCurve]
      .map((p) => ({
        date: p.date,
        day: dayOfWeek(p.date),
        pnl: p.pnl,
        trades: p.trades,
        cumulative: p.equity,
      }))
      .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [equityCurve]);

  const rows = useMemo(
    () => (activeOnly ? allRows.filter((r) => r.trades > 0) : allRows),
    [allRows, activeOnly],
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const hiddenCount = allRows.length - rows.length;

  if (allRows.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
        No daily activity in this window.
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => { setActiveOnly(e.target.checked); setPage(0); }}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Show only active days
        </label>
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {activeOnly && hiddenCount > 0
            ? `${rows.length} active day${rows.length === 1 ? '' : 's'} (${hiddenCount} zero-trade day${hiddenCount === 1 ? '' : 's'} hidden)`
            : `${rows.length} day${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
          No active trading days in this window. Uncheck the filter to see every day.
        </div>
      ) : (
      <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Day
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Daily P&amp;L
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Trades
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Cumulative
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {pageRows.map((r) => (
              <tr key={r.date} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                  {r.date}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {r.day}
                </td>
                <td className={cn(
                  'px-4 py-3 text-sm text-right tabular-nums',
                  pnlClass(r.pnl),
                )}>
                  {fmtInr(r.pnl)}
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700 dark:text-gray-200">
                  {r.trades}
                </td>
                <td className={cn(
                  'px-4 py-3 text-sm text-right tabular-nums',
                  pnlClass(r.cumulative),
                )}>
                  {fmtInr(r.cumulative)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Page {safePage + 1} of {totalPages} · {rows.length} day{rows.length === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
              onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage === totalPages - 1}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default MarketplaceDailyPnlTable;
